import { SITE } from '@/app/constants';
import type { EarnActivity } from './_apis';

export type PlatformKey =
  | 'youtube'
  | 'tiktok'
  | 'x'
  | 'instagram'
  | 'linkedin'
  | 'xiaohongshu'
  | 'bilibili'
  | 'wechat'
  | 'douyin';

const CHINA_EARN_LEARN_MORE_URL = 'https://shengshu.feishu.cn/docx/DjPVdO7AXovFaLxACbNcJ3rgnxc';
const GLOBAL_EARN_LEARN_MORE_URL = 'https://www.vidu.com/activity?id=3337027127191000';

export const EARN_LEARN_MORE_URL = SITE === 'china' ? CHINA_EARN_LEARN_MORE_URL : GLOBAL_EARN_LEARN_MORE_URL;

const ENDED_PHASES: Array<EarnActivity['phase']> = ['awarded', 'completed'];
const ACTIVE_PHASES: Array<EarnActivity['phase']> = ['in_progress', 'in_submission', 'in_evaluation'];
const REWARD_VISIBLE_DELAY_MS = 48 * 60 * 60 * 1000;

const hasHttpProtocol = (url: URL) => url.protocol === 'http:' || url.protocol === 'https:';

const parseTimestamp = (value?: string) => {
  if (!value) return undefined;

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const formatEarnDateTime = (value?: string) => {
  const timestamp = parseTimestamp(value);
  if (typeof timestamp !== 'number') return '';

  const date = new Date(timestamp);
  const pad = (unit: number) => String(unit).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const getEarnActivityTimeRange = (activity: Pick<EarnActivity, 'start_time' | 'end_time'>) => {
  const startTime = formatEarnDateTime(activity.start_time);
  const endTime = formatEarnDateTime(activity.end_time);

  return [startTime, endTime].filter(Boolean).join(' - ');
};

export const isEarnActivityEnded = (activity: Pick<EarnActivity, 'phase' | 'end_time'>, now = Date.now()) => {
  if (ENDED_PHASES.includes(activity.phase)) return true;

  const endTime = parseTimestamp(activity.end_time);
  return typeof endTime === 'number' && endTime <= now;
};

export const isEarnActivityInProgress = (
  activity: Pick<EarnActivity, 'phase' | 'start_time' | 'end_time'>,
  now = Date.now(),
) => {
  if (isEarnActivityEnded(activity, now)) return false;
  if (ACTIVE_PHASES.includes(activity.phase)) return true;
  if (activity.phase === 'not_started') return false;

  const startTime = parseTimestamp(activity.start_time);
  const endTime = parseTimestamp(activity.end_time);
  if (typeof startTime === 'number' && typeof endTime === 'number') {
    return startTime <= now && now < endTime;
  }

  if (typeof startTime === 'number') return startTime <= now;
  if (typeof endTime === 'number') return now < endTime;

  return false;
};

export const getEarnActivityStatus = (
  activity: Pick<EarnActivity, 'phase' | 'start_time' | 'end_time'>,
  now = Date.now(),
) => {
  if (activity.phase === 'not_started') return 'not_started';

  return isEarnActivityInProgress(activity, now) ? 'in_progress' : 'ended';
};

export const canShowEarnReward = (activityEndTime?: string, now = Date.now()) => {
  const endTime = parseTimestamp(activityEndTime);
  return typeof endTime === 'number' && now >= endTime + REWARD_VISIBLE_DELAY_MS;
};

export const isValidWorkUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const isValidApplyPlatformUrl = (platform: PlatformKey, rawUrl: string) => {
  const url = rawUrl.trim();

  if (!url || /\s/.test(url)) return false;

  if (platform === 'wechat') {
    if (/^https?:\/\//i.test(url)) {
      try {
        return /(^|\.)channels\.wechat\.com$|(^|\.)weixin\.qq\.com$/.test(new URL(url).hostname.toLowerCase());
      } catch {
        return false;
      }
    }

    return /^sph[A-Za-z0-9]+$/.test(url);
  }

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsedUrl.pathname;

    if (!hasHttpProtocol(parsedUrl)) return false;

    if (platform === 'youtube') {
      return host === 'youtube.com' && /^\/channel\/UC[^/]+\/?$/.test(path);
    }

    if (platform === 'tiktok') {
      return host === 'tiktok.com' && /^\/@[^/]+\/?$/.test(path);
    }

    if (platform === 'x') {
      return host === 'x.com' && /^\/[^/]+\/?$/.test(path);
    }

    if (platform === 'instagram') {
      return host === 'instagram.com' && /^\/[^/]+\/?$/.test(path);
    }

    if (platform === 'linkedin') {
      return host === 'linkedin.com' && /^\/in\/[^/]+\/?$/.test(path);
    }

    if (platform === 'xiaohongshu') {
      return host === 'xiaohongshu.com' && /^\/user\/profile\/[^/]+\/?$/.test(path);
    }

    if (platform === 'bilibili') {
      return host === 'space.bilibili.com' && /^\/\d+\/?$/.test(path);
    }

    if (platform === 'douyin') {
      return host === 'douyin.com' && /^\/user\/[^/]+\/?$/.test(path);
    }

    return false;
  } catch {
    return false;
  }
};
